import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
}

interface SiteConfig {
  _id: string;
  userId: string;
  siteUrl: string;
  backendUrl: string;
  siteDescription: string;
  selectedHoneypots?: string[];
  honeypotPricing?: { [key: string]: number };
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  siteConfig: SiteConfig | null;
  login: (email: string, password: string) => Promise<any>;
  signup: (email: string, password: string, name: string) => Promise<any>;
  logout: () => void;
  updateSiteConfig: (siteUrl: string, backendUrl: string, siteDescription: string, selectedHoneypots?: string[]) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const userData = await api.getCurrentUser(token);
          setUser(userData);
          
          // Load site config (404 is expected if not configured yet)
          try {
            const config = await api.getSiteConfig(token);
            setSiteConfig(config);
          } catch (error) {
            // Site config not found is not an error - user just hasn't configured it yet
            setSiteConfig(null);
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email: string, password: string) => {
    const response = await api.login(email, password);
    setToken(response.token);
    setUser(response.user);
    localStorage.setItem('token', response.token);
    
    // Load site config after login (404 is expected if not configured yet)
    try {
      const config = await api.getSiteConfig(response.token);
      setSiteConfig(config);
    } catch (error) {
      // Site config not found is not an error
      setSiteConfig(null);
    }
    
    return response;
  };

  const signup = async (email: string, password: string, name: string) => {
    const response = await api.signup(email, password, name);
    setToken(response.token);
    setUser(response.user);
    localStorage.setItem('token', response.token);
    
    // Load site config after signup (404 is expected for new users)
    try {
      const config = await api.getSiteConfig(response.token);
      setSiteConfig(config);
    } catch (error) {
      // Site config not found is expected for new signups
      setSiteConfig(null);
    }
    
    return response;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setSiteConfig(null);
    localStorage.removeItem('token');
  };

  const updateSiteConfig = async (siteUrl: string, backendUrl: string, siteDescription: string, selectedHoneypots?: string[]) => {
    const config = await api.saveSiteConfig(token!, siteUrl, backendUrl, siteDescription, selectedHoneypots);
    setSiteConfig(config);
    return config;
  };

  const value = {
    user,
    token,
    loading,
    siteConfig,
    login,
    signup,
    logout,
    updateSiteConfig,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
